/**
 * Recover a photo's capture date from its filename when EXIF metadata is missing.
 *
 * Phone cameras and messaging apps encode the capture date (and usually the time)
 * directly in the filename. Google Photos downloads, WhatsApp, and gallery/share
 * exports strip EXIF but keep these names, so the filename is a high-success
 * fallback for chronological ordering.
 *
 * Pure and deterministic: the date components are interpreted as UTC, so the same
 * filename always yields the same ISO string regardless of where this runs (no
 * dependence on the host timezone).
 *
 * Supported shapes (first match wins):
 *   20260515_220826.jpg              Samsung / generic        -> date + time
 *   IMG_20260515_220826.jpg          prefixed                 -> date + time
 *   PXL_20260515_220826123.jpg       Google Pixel (+ ms)      -> date + time
 *   Screenshot_20260515-220826_x.jpg Android screenshot       -> date + time
 *   IMG-20260515-WA0001.jpg          WhatsApp (no time)       -> date @ noon UTC
 *   20260515.jpg                     bare date                -> date @ noon UTC
 *
 * Returns an ISO-8601 UTC string, or null when no valid date is present. Only
 * years 2000–2099 are accepted and the calendar date must be real (e.g.
 * 2026-02-30 is rejected), so random numbers never masquerade as dates.
 */
export function dateFromFilename(filename: string): string | null {
  if (!filename) return null;

  // YYYYMMDD, optionally followed by a separator and HHMMSS. Trailing digits
  // after the time (e.g. Pixel's milliseconds) are ignored. The date must start
  // the string or be preceded by a non-digit, so a date is never matched inside
  // a longer number. No lookbehind — keeps Safari < 16.4 working.
  const m = filename.match(
    /(?:^|[^0-9])(20\d{2})(\d{2})(\d{2})(?:[ _T-]?(\d{2})(\d{2})(\d{2}))?/,
  );
  if (!m) return null;

  const year = Number(m[1]);
  const month = Number(m[2]); // 1-12
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  // Date-only names default to noon UTC (avoids day-boundary flips when displayed).
  let hh = 12;
  let mm = 0;
  let ss = 0;
  if (m[4] !== undefined) {
    const h = Number(m[4]);
    const mi = Number(m[5]);
    const s = Number(m[6]);
    if (h <= 23 && mi <= 59 && s <= 59) {
      hh = h;
      mm = mi;
      ss = s;
    }
    // An out-of-range time is ignored; the date is still good (noon).
  }

  const dt = new Date(Date.UTC(year, month - 1, day, hh, mm, ss));
  // Reject impossible calendar dates (e.g. Feb 30 rolls over to Mar 2).
  if (
    dt.getUTCFullYear() !== year ||
    dt.getUTCMonth() !== month - 1 ||
    dt.getUTCDate() !== day
  ) {
    return null;
  }
  return dt.toISOString();
}
